import type { SubscriptionTier, SubscriptionLevel } from './checkout-tracking';
import type { PricingVersion } from './pricing/types';
import { isAttemptId, isPricingVersion } from './pricing/validation';
import { safeFunnelAttribution, type FunnelAttribution } from './pricing/attribution';
export { safeFunnelAttribution, type FunnelAttribution } from './pricing/attribution';

/** Browser funnel contract v1. No credentials, raw query strings or inferred attribution. */
export type FunnelEvent = 'Signup Begin' | 'Checkout Begin';
export type FunnelSurface = 'modal' | 'standalone' | 'iframe';
export interface FunnelOpening {
  id: string;
  /** Same-origin legacy parent exposure, captured before the iframe opens. */
  pricing_catalog_version?: PricingVersion | null;
  experiments?: { name: string; version: number; variant: string }[];
  events: Partial<Record<FunnelEvent, Record<string, unknown>>>;
}
export function createFunnelOpening(): FunnelOpening {
  return { id: analyticsUuid(), events: {} };
}

/** Non-credential IDs: old browsers and blocked crypto must not break the UI. */
export function analyticsUuid(): string {
  try {
    if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  } catch {
    /* Fall through to the compatible generator. */
  }
  const bytes = new Uint8Array(16);
  try {
    crypto.getRandomValues(bytes);
  } catch {
    for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/** Keep optional telemetry outside rendering and authentication failure paths. */
export function safelyTrack(action: () => void): void {
  try {
    action();
  } catch {
    /* Analytics must never interrupt a user action. */
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function isOpening(value: unknown, id: string): value is FunnelOpening {
  if (!isRecord(value) || value.id !== id || !isRecord(value.events)) return false;
  if (
    !Object.entries(value.events).every(
      ([event, payload]) =>
        (event === 'Signup Begin' || event === 'Checkout Begin') && isRecord(payload),
    )
  )
    return false;
  return (
    value.experiments === undefined ||
    (Array.isArray(value.experiments) &&
      value.experiments.every(
        (assignment) =>
          isRecord(assignment) &&
          typeof assignment.name === 'string' &&
          typeof assignment.version === 'number' &&
          Number.isFinite(assignment.version) &&
          typeof assignment.variant === 'string',
      ))
  );
}

const PLAN_INTENTS: Record<SubscriptionTier, true> = { free: true, pro: true, plus: true };
const PLAN_DURATIONS: Record<SubscriptionLevel, true> = {
  monthly: true,
  '1year': true,
  '2year': true,
  '3year': true,
  '5year': true,
  lifetime: true,
};

/** Keep the opening ID even before a start; omit unknown exposure on API/completion payloads. */
export function funnelAttribution(opening: FunnelOpening): FunnelAttribution {
  const first = Object.values(opening.events)[0];
  return safeFunnelAttribution({ ...first, opening_id: opening.id });
}

let pricingExposure: { path: string; version: PricingVersion } | undefined;
export function recordPricingExposure(version: unknown): void {
  // Only displayed offers establish exposure. Unknown page hits must never erase it.
  if (isPricingVersion(version))
    pricingExposure = { path: window.location.pathname, version };
}
export function currentPricingExposure(): PricingVersion | null {
  return pricingExposure?.path === window.location.pathname ? pricingExposure.version : null;
}

let documentPath: string | undefined;
let documentOpening: FunnelOpening | undefined;
/** Rotate explicitly between independent documents/tests, without inventing a navigation. */
export function resetDocumentFunnelOpening(): void {
  documentPath = undefined;
  documentOpening = undefined;
  pricingExposure = undefined;
}
export function documentFunnelOpening(path = window.location.pathname): FunnelOpening {
  if (!documentOpening || documentPath !== path) {
    documentPath = path;
    documentOpening = createFunnelOpening();
    // Same-origin legacy parent supplies a fresh, non-credential opening ID.
    const id = new URLSearchParams(window.location.search).get('funnel_opening');
    if (window.self !== window.top && isAttemptId(id)) {
      try {
        const record = JSON.parse(
          window.sessionStorage.getItem(`pz_checkout_opening:${id}`) || 'null',
        );
        if (isOpening(record, id)) documentOpening = record;
      } catch {
        /* No parent context: use a local opening and unknown assignment. */
      }
    }
  }
  return documentOpening!;
}

export function deviceClass(): 'mobile' | 'tablet' | 'desktop' | 'unknown' {
  if (typeof navigator === 'undefined' || !navigator.userAgent) return 'unknown';
  if (
    /iPad|Tablet|Android(?!.*Mobile)/i.test(navigator.userAgent) ||
    (/Macintosh/i.test(navigator.userAgent) && navigator.maxTouchPoints > 1)
  )
    return 'tablet';
  return /Mobi|iPhone|iPod/i.test(navigator.userAgent) ? 'mobile' : 'desktop';
}

const ACQUISITION_KEY = 'pz_funnel_acquisition_v1';
const CAMPAIGN_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'email_campaign',
] as const;
let acquisition: Record<string, string> | undefined;
export function acquisitionContext(): Record<string, string> {
  if (typeof window === 'undefined') return {};
  if (acquisition) return acquisition;
  try {
    const stored = JSON.parse(window.sessionStorage.getItem(ACQUISITION_KEY) || 'null');
    if (stored && typeof stored === 'object') {
      acquisition = {};
      for (const key of ['landing_path', 'landing_referrer', ...CAMPAIGN_KEYS]) {
        if (typeof stored[key] === 'string') acquisition[key] = stored[key].slice(0, 512);
      }
      return acquisition;
    }
  } catch {
    /* Storage is optional. */
  }
  // An iframe's referrer describes its host UI, not the visitor's acquisition.
  // Auth/callback/recovery URLs are never landing pages for this contract.
  if (
    window.self !== window.top ||
    /^\/(auth|api|reset-password|forgot-password|checkout)(\/|$)/.test(window.location.pathname)
  )
    return {};
  acquisition = { landing_path: window.location.pathname };
  if (document.referrer) acquisition.landing_referrer = document.referrer.replace(/[?#].*$/, '');
  const params = new URLSearchParams(window.location.search);
  for (const key of CAMPAIGN_KEYS) {
    const value = params.get(key === 'email_campaign' ? 'ec' : key);
    if (value) acquisition[key] = value.slice(0, 256);
  }
  try {
    window.sessionStorage.setItem(ACQUISITION_KEY, JSON.stringify(acquisition));
  } catch {
    /* Optional. */
  }
  return acquisition;
}

export function clearAcquisitionContext(): void {
  acquisition = undefined;
  try {
    window.sessionStorage.removeItem(ACQUISITION_KEY);
  } catch {
    /* Optional. */
  }
}

/** Call at availability, once per event per opening. SDK owns delivery/retries. */
export function emitFunnelStart(
  tracker: { trackEvent: (event: string, properties: Record<string, unknown>) => void },
  opening: FunnelOpening,
  event: FunnelEvent,
  properties: Record<string, unknown>,
): void {
  safelyTrack(() => {
    if (opening.events[event]) return;
    const id = analyticsUuid();
    const payload = {
      ...acquisitionContext(),
      ...properties,
      // A plan edit or cutover cannot replace this opening's original exposure.
      pricing_catalog_version:
        Object.values(opening.events)[0]?.pricing_catalog_version ??
        (Object.values(opening.events).length
          ? null
          : (properties.pricing_catalog_version ?? null)),
      plan_intent: Object.prototype.hasOwnProperty.call(
        PLAN_INTENTS,
        String(properties.plan_intent),
      )
        ? properties.plan_intent
        : null,
      plan_duration: Object.prototype.hasOwnProperty.call(
        PLAN_DURATIONS,
        String(properties.plan_duration),
      )
        ? properties.plan_duration
        : null,
      client_flow: 'penzu-next',
      event_schema_version: 1,
      opening_id: opening.id,
      event_id: id,
      $insert_id: id,
      time: Math.floor(Date.now() / 1000),
      device_class: deviceClass(),
    };
    opening.events[event] = payload;
    try {
      if (window.self !== window.top)
        window.sessionStorage.setItem(`pz_checkout_opening:${opening.id}`, JSON.stringify(opening));
    } catch {
      /* Optional persistence; in-memory deduplication still works. */
    }
    tracker.trackEvent(event, payload);
  });
}
